import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { clearToken, getRefreshToken, getToken, setTokens } from './auth';

export const BaseUrl = "https://api.birjandii.ir";

const Api = axios.create({
  baseURL: BaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

Api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// one refresh at a time, shared by every request that got a 401 meanwhile
let refreshing: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  const params = new URLSearchParams({ grant_type: 'refresh_token', refresh_token: refreshToken });
  try {
    // plain axios: this call must not go through the interceptors below
    const res = await axios.post(`${BaseUrl}/api/Account/Token`, params, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    setTokens(res.data.access_token, res.data.refresh_token);
    return res.data.access_token as string;
  } catch {
    return null;
  }
}

// An expired access token is renewed once with the refresh token; if that fails
// the admin is sent back to the login page.
Api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    const onAdminPage = window.location.pathname.toLowerCase().startsWith('/admin');

    if (error.response?.status === 401 && original && !original._retried && onAdminPage) {
      original._retried = true;
      refreshing ??= refreshAccessToken().finally(() => {
        refreshing = null;
      });
      const token = await refreshing;
      if (token) {
        original.headers.Authorization = `Bearer ${token}`;
        return Api(original);
      }
      clearToken();
      window.location.assign('/login');
    }
    return Promise.reject(error);
  }
);

export default Api;
